import { useState } from 'react';
import { Link } from 'react-router-dom';
import { melbourneDate } from '../../../shared/dates';
import type { OutreachClosedReason, OutreachLeadStatus } from '../../../shared/types';
import { formatDate, formatDayMonth, TrashAction } from '../../components/primitives';
import { Sheet, SheetButton, SheetRow } from '../../components/Sheet';
import { outreachRank } from '../../lib/outreachMobile';
import { useIsMobile } from '../../lib/useIsMobile';
import { STATUS_LABELS, VERTICAL_LABELS, STATUS_BUTTONS, COMPLIANCE_NOTE } from './constants';
import { statusLabel, statusPillClass, videoOpportunities, decisionMaker, leadLinks, publishedContacts, joinWords } from './helpers';
import { ScoreBreakdown } from './ScoreBreakdown';
import type { RankedLead, OutreachActions } from './types';
import { ExternalLink, FoundHere } from './chrome';

// ── Dossier drawer ───────────────────────────────────────────────────────

export function OutreachDossier({
  entry,
  actions,
  onDismiss,
}: {
  entry: RankedLead;
  actions: OutreachActions;
  onDismiss: () => void;
}) {
  const isMobile = useIsMobile();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { lead, rank } = entry;
  const [closePicker, setClosePicker] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const opportunities = videoOpportunities(lead);
  const maker = decisionMaker(lead);
  const contacts = publishedContacts(lead);
  const { pills, missing } = leadLinks(lead);
  const manualCheck = contacts.manualCheckUrl ?? pills.find((p) => p.label === 'Website')?.href ?? null;

  const setStatus = (status: OutreachLeadStatus, closedReason?: OutreachClosedReason) => {
    setClosePicker(false);
    actions.onStatus(lead, status, closedReason);
  };

  const dossierBody = (
      <div className="outreach-drawer-body">
        {lead.promoted_lead_id && (
          <div className="field">
            <div className="field-label">In the Leads pipeline</div>
            <div className="field-value">
              <Link to={`/leads/${lead.promoted_lead_id}`}>{lead.name} enquiry ↗</Link>
            </div>
            <div className="field-value dim outreach-fineprint">
              {lead.promoted_at !== null
                ? `Promoted ${formatDate(melbourneDate(lead.promoted_at))}. One enquiry only, promoting again is blocked.`
                : 'One enquiry only, promoting again is blocked.'}
            </div>
          </div>
        )}

        {lead.why_they_fit && (
          <div className="field">
            <div className="field-label">Why they fit</div>
            <div className="outreach-prose">{lead.why_they_fit}</div>
          </div>
        )}

        {opportunities.length > 0 && (
          <div className="field">
            <div className="field-label">Video opportunity map</div>
            {opportunities.map((o, index) => (
              <div className="outreach-vo" key={`${index}-${o.service}`}>
                <span className="outreach-vo-dot" />
                <div>
                  <div className="outreach-vo-svc">{o.service}</div>
                  {o.why && <div className="outreach-vo-why">{o.why}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {lead.outreach_angle && (
          <div className="field">
            <div className="field-label">Outreach angle</div>
            <div className="notes-box">{lead.outreach_angle}</div>
          </div>
        )}

        <div className="field">
          <div className="field-label">Decision maker</div>
          {maker.name ? (
            <>
              <div className="field-value">
                {maker.name}
                {maker.role && <span className="outreach-role"> · {maker.role}</span>}
              </div>
              <div className="outreach-contact">
                {maker.linkedinUrl && (
                  <ExternalLink className="outreach-linkpill" href={maker.linkedinUrl}>
                    LinkedIn, manual look ↗
                  </ExternalLink>
                )}
                {maker.sourceUrl && <FoundHere href={maker.sourceUrl} />}
              </div>
            </>
          ) : (
            <>
              <div className="field-value dim">Nobody named on any crawled page.</div>
              {manualCheck && (
                <div className="outreach-contact">
                  <ExternalLink className="outreach-found" href={manualCheck}>
                    check the team page manually ↗
                  </ExternalLink>
                </div>
              )}
            </>
          )}
        </div>

        <div className="field">
          <div className="field-label">Links</div>
          <div className="outreach-linkpills">
            {pills.map((pill) => (
              <ExternalLink className="outreach-linkpill" href={pill.href} key={pill.label}>
                {`${pill.label} ↗`}
              </ExternalLink>
            ))}
            {missing.length > 0 && (
              <span className="outreach-missing">no {joinWords(missing)} found</span>
            )}
          </div>
        </div>

        <div className="field">
          <div className="field-label">Published contacts</div>
          {contacts.emails.map((email, index) => (
            <div className="outreach-contact" key={`${index}-${email.value}`}>
              <span className="outreach-contact-val">{email.value}</span>
              {email.sourceUrl && <FoundHere href={email.sourceUrl} />}
            </div>
          ))}
          {contacts.emails.length === 0 && (
            <div className="outreach-contact">
              <span className="outreach-missing">No email published on any crawled page.</span>
              {manualCheck && (
                <ExternalLink className="outreach-found" href={manualCheck}>
                  check contact page manually ↗
                </ExternalLink>
              )}
            </div>
          )}
          {contacts.phone ? (
            <div className="outreach-contact">
              <span className="outreach-contact-val">{contacts.phone.value}</span>
              {contacts.phone.sourceUrl && <FoundHere href={contacts.phone.sourceUrl} />}
            </div>
          ) : (
            <div className="outreach-contact">
              <span className="outreach-missing">No phone published on any crawled page.</span>
            </div>
          )}
        </div>

        <div className="field">
          <button
            type="button"
            className="rail-group-header"
            aria-expanded={scoreOpen}
            onClick={() => setScoreOpen((o) => !o)}
          >
            <span className="field-label rail-group-title">Score breakdown</span>
            <span className="rail-group-badge">{lead.score} / 100</span>
            <span className="rail-group-chevron">{scoreOpen ? '▾' : '▸'}</span>
          </button>
          {scoreOpen && <ScoreBreakdown lead={lead} />}
        </div>
      </div>
  );
  // §12 frame B: the drawer becomes a full sheet with the same field order.
  // The eyebrow, name and score/band/status summary ride in the sheet header
  // and its pinned strip; the status ladder, Promote and Delete pin to the
  // footer, so the body scrolls under both.
  if (isMobile)
    return (
      <>
        <Sheet
          open
          variant="full"
          className="outreach-mobile-dossier"
          title={lead.name}
          context={`${lead.verdict} · rank ${outreachRank(rank)} · pushed ${formatDayMonth(lead.run_date)}`}
          onClose={onDismiss}
          rightAction={<SheetButton onClick={onDismiss}>Close</SheetButton>}
          pinned={
            <div className="outreach-mobile-summary">
              <div className="detail-sub">
                {lead.suburb} · {VERTICAL_LABELS[lead.vertical]}
                {lead.domain ? ` · ${lead.domain}` : ''}
              </div>
              <div className="outreach-drawer-meta">
                <span className="outreach-score-big">{lead.score}</span>
                {lead.band && (
                  <span className={`pill${lead.band.includes('retainer') ? ' gold' : ''}`}>
                    {lead.band}
                  </span>
                )}
                <span className={statusPillClass(lead)}>{statusLabel(lead)}</span>
              </div>
            </div>
          }
          footer={
            <div className="outreach-mobile-footer">
              <div className="chiprow">
                {STATUS_BUTTONS.map((status) => (
                  <button
                    type="button"
                    key={status}
                    className={`c${lead.status === status ? ' on' : ''}`}
                    aria-pressed={lead.status === status}
                    onClick={() => setStatus(status)}
                  >
                    {STATUS_LABELS[status]}
                  </button>
                ))}
                <button
                  type="button"
                  className={`c${lead.status === 'closed' ? ' on' : ''}`}
                  aria-expanded={closePicker}
                  onClick={() => setClosePicker(true)}
                >
                  closed ▾
                </button>
              </div>
              <div className="outreach-mobile-buttons">
                {lead.promoted_lead_id ? (
                  <Link className="cbtn wide" to={`/leads/${lead.promoted_lead_id}`}>
                    in Leads ↗
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="cbtn wide pri"
                    onClick={() => actions.onPromote(lead)}
                  >
                    Promote to Leads →
                  </button>
                )}
                <button type="button" className="cbtn dng" onClick={() => setDeleteConfirm(true)}>
                  Delete
                </button>
              </div>
            </div>
          }
        >
          {dossierBody}
          <p className="outreach-compliance">{COMPLIANCE_NOTE}</p>
        </Sheet>

        <Sheet open={closePicker} variant="half" title="Close as" onClose={() => setClosePicker(false)}>
          <SheetRow label="Won" onSelect={() => setStatus('closed', 'won')} />
          <SheetRow label="Not a fit" onSelect={() => setStatus('closed', 'not_a_fit')} />
        </Sheet>

        <Sheet
          open={deleteConfirm}
          variant="half"
          title="Delete lead?"
          context={lead.name}
          onClose={() => setDeleteConfirm(false)}
          footer={
            <div className="outreach-mobile-buttons">
              <button type="button" className="cbtn wide" onClick={() => setDeleteConfirm(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="cbtn wide dng"
                onClick={() => {
                  setDeleteConfirm(false);
                  actions.onDelete(lead);
                }}
              >
                Delete lead
              </button>
            </div>
          }
        >
          <p className="outreach-mobile-confirm">
            This removes the outreach lead and its dossier.
          </p>
        </Sheet>
      </>
    );

  return (
    <aside className="outreach-drawer" data-outreach-drawer>
      <div className="outreach-drawer-head">
        <div className="outreach-eyebrow">
          {lead.verdict} · rank {String(rank).padStart(2, '0')} · pushed {formatDate(lead.run_date)}
        </div>
        <div className="detail-name">{lead.name}</div>
        <div className="detail-sub">
          {lead.suburb} · {VERTICAL_LABELS[lead.vertical]}
          {lead.domain ? ` · ${lead.domain}` : ''}
        </div>
        <div className="outreach-drawer-meta">
          <span className="outreach-score-big">{lead.score}</span>
          {lead.band && <span className={`pill${lead.band.includes('retainer') ? ' gold' : ''}`}>{lead.band}</span>}
          <span className={statusPillClass(lead)}>{statusLabel(lead)}</span>
        </div>
        <button type="button" className="outreach-close" onClick={onDismiss}>
          ESC
        </button>
      </div>

      {dossierBody}

      <div className="outreach-drawer-foot">
        <div className="outreach-statusrow">
          <span className="f-label outreach-status-label">status</span>
          {STATUS_BUTTONS.map((status) => (
            <button
              type="button"
              key={status}
              className={`btn${lead.status === status ? ' on' : ''}`}
              onClick={() => setStatus(status)}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
          <span className="outreach-pop-anchor">
            <button
              type="button"
              className={`btn${lead.status === 'closed' ? ' on' : ''}`}
              aria-expanded={closePicker}
              onClick={() => setClosePicker((o) => !o)}
            >
              closed ▾
            </button>
            {closePicker && (
              <div className="outreach-pop">
                <div className="outreach-pop-title">Close as</div>
                <button type="button" className="outreach-pop-opt" onClick={() => setStatus('closed', 'won')}>
                  Won
                </button>
                <button
                  type="button"
                  className="outreach-pop-opt"
                  onClick={() => setStatus('closed', 'not_a_fit')}
                >
                  Not a fit
                </button>
              </div>
            )}
          </span>
        </div>
        <div className="outreach-actionrow">
          {lead.promoted_lead_id ? (
            <Link className="btn outreach-muted" to={`/leads/${lead.promoted_lead_id}`}>
              Promoted ↗
            </Link>
          ) : (
            <button type="button" className="btn primary" onClick={() => actions.onPromote(lead)}>
              Promote to Leads
            </button>
          )}
          <span className="row-spacer" />
          <TrashAction label="Delete lead" onConfirm={() => actions.onDelete(lead)} />
        </div>
        <p className="outreach-compliance">{COMPLIANCE_NOTE}</p>
      </div>
    </aside>
  );
}
